import { useCallback, useEffect, useRef, useState } from 'react';
import type { Agent } from '../../agents/registry.ts';
import { isNetworkError } from '../../lib/networkError.ts';
import {
  getOrCreateSessionId,
  setSessionId,
  startNewSession,
} from '../../agents/sessionStore.ts';
import {
  deleteSession as apiDeleteSession,
  getSession,
  listSessions,
  type SessionSummary,
  type SessionTranscriptTurn,
} from '../../agents/sessionApi.ts';
import { recordClientFailure } from '../../telemetry/rum.ts';
import { failureKind } from '../../telemetry/failureKind.ts';

/**
 * Outcome of {@link UseSessions.reloadCurrent}.
 *
 * `reloadCurrent` used to return `void`, which made it the silent last link of
 * the workflow-delivery chain: a finished run writes its result into the
 * session transcript server-side, and this call is the ONLY thing that pulls it
 * onto the user's screen. When it failed, the result simply never appeared and
 * nothing anywhere recorded that — so the caller can now see what happened and
 * say so.
 */
export interface ReloadOutcome {
  /** The transcript was re-pulled and handed to the chat. */
  readonly ok: boolean;
  /** The restored turns (empty when `ok` is false). Lets the caller check
   *  whether the turn it was waiting for actually landed. */
  readonly turns: readonly SessionTranscriptTurn[];
}

/**
 * Owns the console's session state: the current session id, the server-backed
 * history list, and the transcript to restore into the chat. Lifted by
 * `AgentConsole`, which feeds `currentSessionId` + `transcript` into
 * `useAgentChat` and the list + actions into `SessionsPanel`.
 *
 * The current id seeds from `sessionStore` (the persisted per-agent id, so the
 * agent keeps server-side continuity across reloads). History loads on mount
 * and refreshes after each action. All async results are guarded against a
 * since-changed agent so a slow fetch can't land on the wrong agent.
 */

/** Shown when a load fails because the server was unreachable. */
const NETWORK_MESSAGE =
  "We couldn't reach the server. Check your connection and try again.";

/**
 * Pick the user-facing message for a failed session load. A network failure
 * (offline, or a raw Cognito identity rejection thrown while fetching the
 * access token) reads as a connectivity problem with a retry; anything else
 * falls back to `fallback`. The raw error never reaches the UI — callers log it
 * to the console for debugging.
 */
function friendlyError(err: unknown, fallback: string): string {
  return isNetworkError(err) ? NETWORK_MESSAGE : fallback;
}
export interface UseSessions {
  readonly sessions: readonly SessionSummary[];
  readonly currentSessionId: string;
  /** Turns to restore into the chat, or null for a fresh/greeting view. */
  readonly transcript: readonly SessionTranscriptTurn[] | null;
  /** Bumped each time a transcript is restored for the *current* session id
   *  without that id changing (resuming the persisted session on reload). Lets
   *  `useAgentChat` re-render the restored transcript even though `sessionId`
   *  stayed the same. */
  readonly restoreKey: number;
  readonly loading: boolean;
  /** True while a freshly-started session is being spun up (the "New session"
   *  action). Lets the chat's placeholder read "Creating…" rather than "Restoring…". */
  readonly creating: boolean;
  readonly error: string | null;
  /** Start a fresh session; the previous one drops into history. */
  newSession: () => void;
  /** Restore a session from history into the foreground. */
  selectSession: (sessionId: string) => void;
  /** Re-pull the CURRENT session's transcript and re-key the chat. The
   *  broken-stream catch-up path: unlike `selectSession`, this works when the
   *  target IS the current session (`selectSession` no-ops on the same id).
   *
   *  Resolves with what happened, so the workflow-delivery path can tell
   *  "the result is now on screen" from "the refresh failed" — those used to
   *  be indistinguishable. */
  reloadCurrent: () => Promise<ReloadOutcome>;
  /** Delete a session server-side; if it was current, fall back to a new one. */
  deleteSession: (sessionId: string) => Promise<void>;
  /** Re-run the on-mount load — backs the Sessions panel's "Retry" button. */
  retry: () => void;
}

export function useSessions(agent: Agent): UseSessions {
  const [sessions, setSessions] = useState<readonly SessionSummary[]>([]);
  const [currentSessionId, setCurrentSessionId] = useState<string>(() =>
    getOrCreateSessionId(agent.slug),
  );
  const [transcript, setTranscript] = useState<readonly SessionTranscriptTurn[] | null>(
    null,
  );
  const [restoreKey, setRestoreKey] = useState(0);
  // Starts true: history loads on mount, so the console shows its "preparing"
  // placeholder from the first render rather than flashing a greeting first.
  const [loading, setLoading] = useState(true);
  // True only while the "New session" action spins up a fresh session — so its
  // placeholder reads "Creating…" instead of the entry/reload "Restoring…".
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Guards async results against a since-changed agent (stale fetch landing).
  const slugRef = useRef(agent.slug);
  slugRef.current = agent.slug;

  const refresh = useCallback(async (slug: Agent['slug']) => {
    setLoading(true);
    setError(null);
    try {
      const rows = await listSessions(slug);
      if (slugRef.current === slug) {
        setSessions(rows);
      }
    } catch (err) {
      if (slugRef.current === slug) {
        console.error('[useSessions] refresh failed', err);
        setError(friendlyError(err, 'Could not load your sessions.'));
      }
    } finally {
      if (slugRef.current === slug) {
        setLoading(false);
      }
    }
  }, []);

  // Resume the persisted session id, load history, and — when that session
  // actually has stored turns — rehydrate its transcript so a reload restores
  // the conversation just like clicking it in the list does. A freshly minted
  // (or idle-expired → new) id isn't in the list, so it keeps the greeting
  // rather than 404-ing on a transcript fetch. Shared by the mount effect and
  // the panel's "Retry" action.
  const load = useCallback(async (slug: Agent['slug']) => {
    const resumedId = getOrCreateSessionId(slug);
    setCurrentSessionId(resumedId);
    setTranscript(null);
    setLoading(true);
    setError(null);
    try {
      const rows = await listSessions(slug);
      if (slugRef.current !== slug) {
        return;
      }
      setSessions(rows);
      if (rows.some((s) => s.sessionId === resumedId)) {
        const restored = await getSession(slug, resumedId);
        if (slugRef.current !== slug) {
          return;
        }
        setTranscript(restored.turns);
        // Re-key useAgentChat: the id is unchanged on reload, so the restored
        // transcript would otherwise never render.
        setRestoreKey((k) => k + 1);
      }
    } catch (err) {
      if (slugRef.current === slug) {
        console.error('[useSessions] load failed', err);
        setError(friendlyError(err, 'Could not load your sessions.'));
      }
    } finally {
      if (slugRef.current === slug) {
        setLoading(false);
      }
    }
  }, []);

  // On agent change / mount: (re)load this agent's history.
  useEffect(() => {
    void load(agent.slug);
  }, [agent.slug, load]);

  const retry = useCallback(() => {
    void load(agent.slug);
  }, [agent.slug, load]);

  const newSession = useCallback(() => {
    const id = startNewSession(agent.slug);
    setCurrentSessionId(id);
    setTranscript(null);
    // Mark the brief spin-up so the chat placeholder reads "Creating…" not
    // "Restoring…" while the history list refetches in the background.
    setCreating(true);
    void refresh(agent.slug).finally(() => setCreating(false));
  }, [agent.slug, refresh]);

  const selectSession = useCallback(
    (sessionId: string) => {
      if (sessionId === currentSessionId) {
        return;
      }
      const slug = agent.slug;
      void (async () => {
        try {
          const restored = await getSession(slug, sessionId);
          if (slugRef.current !== slug) {
            return;
          }
          // Persist + swap to the foreground together so useAgentChat's mount
          // effect (keyed on sessionId) re-runs with the restored transcript
          // already present.
          setSessionId(slug, sessionId);
          setTranscript(restored.turns);
          setCurrentSessionId(sessionId);
          void refresh(slug);
        } catch (err) {
          if (slugRef.current === slug) {
            console.error('[useSessions] open session failed', err);
            setError(friendlyError(err, 'Could not open that session.'));
          }
        }
      })();
    },
    [agent.slug, currentSessionId, refresh],
  );

  const reloadCurrent = useCallback(async (): Promise<ReloadOutcome> => {
    const slug = agent.slug;
    const sessionId = currentSessionId;
    try {
      const restored = await getSession(slug, sessionId);
      if (slugRef.current !== slug) {
        // The user switched agents mid-flight; dropping the result is correct,
        // but it is not a delivery either.
        return { ok: false, turns: [] };
      }
      setTranscript(restored.turns);
      // Same id, so only the restoreKey bump re-keys useAgentChat.
      setRestoreKey((k) => k + 1);
      return { ok: true, turns: restored.turns };
    } catch (err) {
      // Best-effort for the user (no error surfaces for a background refresh),
      // but NOT best-effort for us: this is the last link of the
      // workflow-delivery chain, and a `console.error` here reaches nobody.
      // A failure means a finished run's result is sitting in S3 unread.
      console.error('[useSessions] reload current failed', err);
      recordClientFailure(
        'session-reload-failed',
        failureKind(err),
      );
      return { ok: false, turns: [] };
    }
  }, [agent.slug, currentSessionId]);

  const deleteSession = useCallback(
    async (sessionId: string) => {
      const slug = agent.slug;
      await apiDeleteSession(slug, sessionId);
      if (slugRef.current !== slug) {
        return;
      }
      setSessions((prev) => prev.filter((s) => s.sessionId !== sessionId));
      // Deleting the active session: fall back to a brand-new one so the chat
      // isn't left pointed at a now-gone transcript.
      if (sessionId === currentSessionId) {
        const id = startNewSession(slug);
        setCurrentSessionId(id);
        setTranscript(null);
      }
    },
    [agent.slug, currentSessionId],
  );

  return {
    sessions,
    currentSessionId,
    transcript,
    restoreKey,
    loading,
    creating,
    error,
    newSession,
    selectSession,
    reloadCurrent,
    deleteSession,
    retry,
  };
}
